import React, { FunctionComponent } from "react";
import { Modal } from "@fluentui/react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { TextField } from "@fluentui/react/lib/TextField";
import { DeleteRegular } from "@fluentui/react-icons";
import HeaderPanel from "../../layout/HeaderPanel";
import FooterPanel from "../../layout/FooterPanel";
import { contentStyles } from "../../layout/ActionModal";
import { RuleInputEditor } from "../../generic/RuleInputEditor";
import { ObjectListItemRow } from "../../coobjects/object-list-panel/ObjectListItemRow";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import {
  AskAiReviewRuleDraft,
  AskAiReviewSaveModalProps,
  AskAiReviewTagDraft,
  AskAiReviewTagTableDisplayItem,
  TAGS_SECTION_ID,
  useAskAiReviewSaveModal,
} from "./AskAiReviewSaveModalHook";
import AccordionListPanel, { AccordionItemData } from "../../generic/accordion/AccordionListPanel";
import { ConsistencyRuleEditor } from "./rule/ConsistencyRuleEditor";
import { RuleType } from "../../interfaces/Rule";

const styles = {
  content: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 132px)",
    padding: "0 0.5rem 74px 0.5rem",
  } as React.CSSProperties,
  scrollArea: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    paddingTop: 8,
    paddingBottom: 8,
  } as React.CSSProperties,
  ruleDeleteButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--color-error, #d13438)",
    cursor: "pointer",
  } as React.CSSProperties,
  ruleDeleteIcon: {
    fontSize: "20px",
  } as React.CSSProperties,
  panelContent: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingBottom: "8px",
  } as React.CSSProperties,
  fieldTitle: {
    fontWeight: 700,
    color: "var(--color-blue)",
    fontSize: "14px",
  } as React.CSSProperties,
  requiredAsterisk: {
    color: "var(--color-error, #a4262c)",
  } as React.CSSProperties,
  statusLabel: {
    fontWeight: 700,
    fontSize: "14px",
  } as React.CSSProperties,
  tagBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  } as React.CSSProperties,
  tagsList: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  } as React.CSSProperties,
  taggedObjectsList: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  } as React.CSSProperties,
  tableCellList: {
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  } as React.CSSProperties,
  taggedItemsPill: {
    backgroundColor: "var(--color-blue)",
    color: "white",
    borderRadius: "999px",
    padding: "2px 10px",
    fontWeight: 700,
    fontSize: "12px",
    lineHeight: "18px",
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  } as React.CSSProperties,
};

export const AskAiReviewSaveModal: FunctionComponent<AskAiReviewSaveModalProps> = (props) => {
  const {
    ruleDrafts,
    tagDrafts,
    referenceTags,
    expandedRuleIds,
    expandedTagSectionIds,
    expandedTagObjectIds,
    expandedTableGroupIds,
    handleRulesAccordionToggle,
    handleTagsAccordionToggle,
    handleTagObjectsAccordionToggle,
    handleTableGroupAccordionToggle,
    handleRuleTitleChange,
    handleRuleScriptChange,
    handleRuleTagIdsChange,
    handleRuleDelete,
    handleTagLabelChange,
    handleTagObjectToggle,
    handleObjectClick,
    handleCancel,
    handleSave,
    getRuleStatusLabel,
    getTaggedItemsLabel,
    getTagDisplayItems,
    getRuleDraftErrors,
    getTagDraftErrors,
    isSaveEnabled,
  } = useAskAiReviewSaveModal(props);

  const hasRules = ruleDrafts.length > 0;
  const hasTags = tagDrafts.length > 0;

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={props.isShown}
      containerClassName={contentStyles.container}
      styles={{ scrollableContent: { height: "100vh" } }}
    >
      <HeaderPanel applyPadding>{formatMessage("cof.validation.button.reviewandsave")}</HeaderPanel>
      <div style={styles.content}>
        <div style={styles.scrollArea}>
          {(hasRules || hasTags) && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <style>{`
                .arsm-panel .alp-item.alp-expanded { background-color: transparent !important; }
                .arsm-panel .alp-item.alp-expanded .fui-AccordionHeader,
                .arsm-panel .alp-item.alp-expanded .fui-AccordionPanel { background-color: transparent !important; }
                .arsm-rules .alp-item.alp-expanded { border-bottom: 2px solid var(--color-blue); border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; }
                .arsm-delete { cursor: pointer; margin-left: auto; }
              `}</style>
              {hasRules && (
                <div className="arsm-panel arsm-rules">
                  <AccordionListPanel<AskAiReviewRuleDraft>
                    items={ruleDrafts.map(
                      (rule): AccordionItemData<AskAiReviewRuleDraft> => ({
                        id: rule.draftKey,
                        title: rule.title,
                        data: rule,
                      })
                    )}
                    filteredItems={ruleDrafts.map(
                      (rule): AccordionItemData<AskAiReviewRuleDraft> => ({
                        id: rule.draftKey,
                        title: rule.title,
                        data: rule,
                      })
                    )}
                    expanded={expandedRuleIds}
                    searchQuery=""
                    actionGroups={[]}
                    onAccordionToggle={handleRulesAccordionToggle}
                    onFilter={() => {}}
                    withFilter={false}
                    expandIconPosition="start"
                    automationIdSuffix="rule"
                    headerStyle={{ color: "var(--color-blue)", fontWeight: 700 }}
                    itemBadge={(item) => (
                      <span
                        className="arsm-delete"
                        role="button"
                        tabIndex={0}
                        data-testid={`rule-delete-${item.id}`}
                        aria-label="Delete rule"
                        style={styles.ruleDeleteButton}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRuleDelete(item.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            handleRuleDelete(item.id);
                          }
                        }}
                      >
                        <DeleteRegular style={styles.ruleDeleteIcon} />
                      </span>
                    )}
                    itemContent={(item) => {
                      const isConsistency = item.data.type === RuleType.CONSISTENCY;
                      const errors = getRuleDraftErrors(item.id);
                      return (
                        <div style={styles.panelContent}>
                          <div style={styles.statusLabel} data-testid={`rule-status-${item.id}`}>
                            {getRuleStatusLabel(item.data.id)}
                          </div>
                          {isConsistency ? (
                            <>
                              <ConsistencyRuleEditor
                                tags={referenceTags.filter((t) => !t.isDeleted)}
                                selectedTagIds={item.data.tagIds ?? []}
                                onChange={(nextTagIds) => handleRuleTagIdsChange(item.id, nextTagIds)}
                                withDescription={false}
                                withFilter={false}
                              />
                              {errors.consistencyError && (
                                <div
                                  style={{ color: "var(--color-error, #a4262c)", fontSize: "12px", marginTop: "4px" }}
                                >
                                  {errors.consistencyError}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div style={styles.fieldTitle}>
                                {formatMessage("cof.validation.title.ruleinputeditor")}
                              </div>
                              <RuleInputEditor
                                value={item.data.script}
                                onChange={(nextScript) => handleRuleScriptChange(item.id, nextScript)}
                                tags={referenceTags}
                              />
                              {errors.scriptError && (
                                <div
                                  style={{ color: "var(--color-error, #a4262c)", fontSize: "12px", marginTop: "4px" }}
                                >
                                  {errors.scriptError}
                                </div>
                              )}
                            </>
                          )}
                          <div style={styles.fieldTitle}>
                            {formatMessage("cof.validation.title.rulename")}{" "}
                            <span style={styles.requiredAsterisk}>*</span>
                          </div>
                          <TextField
                            data-testid={`rule-name-${item.id}`}
                            value={item.data.title}
                            onChange={(_event, nextTitle) => handleRuleTitleChange(item.id, nextTitle ?? "")}
                            aria-label={formatMessage("cof.validation.title.rulename")}
                            errorMessage={errors.titleError}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
              )}
              {hasTags && (
                <div className="arsm-panel">
                  <AccordionListPanel<AskAiReviewTagDraft[]>
                    items={[{ id: TAGS_SECTION_ID, title: formatMessage("cof.base.tags"), data: tagDrafts }]}
                    filteredItems={[{ id: TAGS_SECTION_ID, title: formatMessage("cof.base.tags"), data: tagDrafts }]}
                    expanded={expandedTagSectionIds}
                    searchQuery=""
                    actionGroups={[]}
                    onAccordionToggle={handleTagsAccordionToggle}
                    onFilter={() => {}}
                    withFilter={false}
                    expandIconPosition="start"
                    automationIdSuffix="tag"
                    itemContent={() => (
                      <div style={styles.tagsList}>
                        {tagDrafts.map((tag) => {
                          const tagDisplayItems = getTagDisplayItems(tag);
                          const tagErrors = getTagDraftErrors(tag.draftKey);

                          return (
                            <div key={tag.draftKey} style={styles.tagBlock}>
                              <TextField
                                data-testid={`tag-name-${tag.draftKey}`}
                                value={tag.label}
                                onChange={(_event, nextLabel) => handleTagLabelChange(tag.draftKey, nextLabel ?? "")}
                                aria-label={formatMessage("cof.validation.title.tagname")}
                                errorMessage={tagErrors.labelError || undefined}
                              />
                              <div style={{ margin: "0 -8px" }}>
                                <AccordionListPanel<AskAiReviewTagDraft>
                                  style={{ padding: "0 8px 0 0" }}
                                  items={[{ id: tag.draftKey, title: "", data: tag }]}
                                  filteredItems={[{ id: tag.draftKey, title: "", data: tag }]}
                                  expanded={expandedTagObjectIds}
                                  searchQuery=""
                                  actionGroups={[]}
                                  onAccordionToggle={handleTagObjectsAccordionToggle}
                                  onFilter={() => {}}
                                  withFilter={false}
                                  expandIconPosition="start"
                                  itemStart={() => (
                                    <span style={styles.taggedItemsPill}>
                                      {getTaggedItemsLabel(tag.selectedObjectIds.length)}
                                    </span>
                                  )}
                                  itemContent={() => (
                                    <div style={styles.taggedObjectsList}>
                                      {tagDisplayItems.map((displayItem) => {
                                        if (displayItem.kind === "object") {
                                          return (
                                            <ObjectListItemRow
                                              key={displayItem.object.id}
                                              objectItem={displayItem.objectItem}
                                              onClick={() => handleObjectClick(displayItem.object)}
                                              onToggleSelect={(_objectItem, selected) =>
                                                handleTagObjectToggle(tag.draftKey, displayItem.object.id, selected)
                                              }
                                              selected={tag.selectedObjectIds.includes(displayItem.object.id)}
                                              showStatusIcons={false}
                                            />
                                          );
                                        }

                                        return (
                                          <AccordionListPanel<AskAiReviewTagTableDisplayItem>
                                            key={displayItem.groupId}
                                            items={[
                                              {
                                                id: displayItem.groupId,
                                                title: displayItem.tableObjectItem.info.objectName,
                                                data: displayItem,
                                              },
                                            ]}
                                            filteredItems={[
                                              {
                                                id: displayItem.groupId,
                                                title: displayItem.tableObjectItem.info.objectName,
                                                data: displayItem,
                                              },
                                            ]}
                                            expanded={expandedTableGroupIds}
                                            searchQuery=""
                                            actionGroups={[]}
                                            onAccordionToggle={(event, data) =>
                                              handleTableGroupAccordionToggle(displayItem.groupId, event, data)
                                            }
                                            onFilter={() => {}}
                                            withFilter={false}
                                            expandIconPosition="start"
                                            headerStyle={{ fontWeight: "normal" }}
                                            itemStart={() => (
                                              <span
                                                className={`${displayItem.tableObjectItem.info.objectType} icon_class`}
                                                style={{ lineHeight: 1 }}
                                              />
                                            )}
                                            itemContent={() => (
                                              <div style={styles.tableCellList}>
                                                {displayItem.cells.map((cell) => (
                                                  <ObjectListItemRow
                                                    key={cell.object.id}
                                                    objectItem={cell.objectItem}
                                                    onClick={() => handleObjectClick(cell.object)}
                                                    onToggleSelect={(_objectItem, selected) =>
                                                      handleTagObjectToggle(tag.draftKey, cell.object.id, selected)
                                                    }
                                                    selected={tag.selectedObjectIds.includes(cell.object.id)}
                                                    showStatusIcons={false}
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          />
                                        );
                                      })}
                                    </div>
                                  )}
                                />
                              </div>
                              {tagErrors.objectsError && (
                                <div
                                  style={{ color: "var(--color-error, #a4262c)", fontSize: "12px", marginTop: "4px" }}
                                >
                                  {tagErrors.objectsError}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <FooterPanel>
        <div style={styles.footer}>
          <DefaultButton
            data-testid="askai-review-cancel"
            onClick={handleCancel}
            text={formatMessage("cof.base.cancel").toUpperCase()}
          />
          <PrimaryButton
            data-testid="askai-review-save"
            onClick={handleSave}
            disabled={!isSaveEnabled}
            text={formatMessage("cof.base.save").toUpperCase()}
          />
        </div>
      </FooterPanel>
    </Modal>
  );
};
